import { useEffect, useMemo, useState } from "react";
import { resolveEntitlements } from "@lucralink/shared";
import { AlertCircle, Download, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useTenant } from "@/lib/hooks/useTenant";
import {
  SCENE_PRESET_OPTIONS,
  useAiImageUsage,
  useGenerateAiImage,
  type GeneratedImageVariant,
} from "@/lib/hooks/useAiImages";
import { AiImageGallery } from "./AiImageGallery";

export interface ImageGeneratorDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly productUrl: string;
}

/** Sheet lateral: gera imagem de produto ambientada (lifestyle) via IA
 *  (Modulo 3 de paridade). Free tem 5 imagens vitalicias (entitlements.ts,
 *  freeAiImages), mesmo teto do Pro - o bloco de CTA de upgrade abaixo so
 *  aparece se algum tier futuro voltar a ter freeAiImages=0. Prompt
 *  customizado so aparece pro tier pro_ai (unico com cota mensal real para
 *  justificar). */
export function ImageGeneratorDrawer({ open, onClose, productUrl }: ImageGeneratorDrawerProps): JSX.Element | null {
  const { data: tenantBilling } = useTenant();
  const tenant = tenantBilling?.tenant ?? null;
  const entitlements = useMemo(
    () => resolveEntitlements(tenant?.plan ?? "free", tenant?.pro_until ?? null),
    [tenant?.plan, tenant?.pro_until],
  );
  const hasAccess = entitlements.freeAiImages > 0 || entitlements.aiImagesPerMonth > 0;
  const canCustomizePrompt = entitlements.aiImagesPerMonth > 0;

  const { data: usage } = useAiImageUsage();
  const { generate, loading, error } = useGenerateAiImage();

  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [presetId, setPresetId] = useState(SCENE_PRESET_OPTIONS[0]!.id);
  const [customPrompt, setCustomPrompt] = useState("");
  const [result, setResult] = useState<GeneratedImageVariant[] | null>(null);

  useEffect(() => {
    if (open) {
      setSourceUrl("");
      setSourceFile(null);
      setReferenceFile(null);
      setPresetId(SCENE_PRESET_OPTIONS[0]!.id);
      setCustomPrompt("");
      setResult(null);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const usageLabel = usage
    ? entitlements.aiImagesPerMonth > 0
      ? `${usage.monthUsed}/${entitlements.aiImagesPerMonth} este mês`
      : `${usage.lifetimeUsed}/${entitlements.freeAiImages} vitalícias`
    : null;

  const canSubmit = Boolean(tenant) && (Boolean(sourceFile) || sourceUrl.trim().length > 0) && !loading;

  const handleGenerate = async (): Promise<void> => {
    if (!tenant) return;
    const useCustom = canCustomizePrompt && customPrompt.trim().length > 0;
    const trimmedUrl = sourceUrl.trim();

    // exactOptionalPropertyTypes: campos opcionais so entram no objeto quando
    // realmente tem valor - atribuir "undefined" explicito nao e o mesmo que
    // omitir a chave para esse modo estrito do TS.
    const output = await generate({
      tenantId: tenant.id,
      productUrl,
      ...(sourceFile ? { sourceImageFile: sourceFile } : trimmedUrl ? { sourceImageUrl: trimmedUrl } : {}),
      ...(referenceFile ? { referenceImageFile: referenceFile } : {}),
      ...(useCustom ? { customPrompt: customPrompt.trim() } : { scenePresetId: presetId }),
    });
    if (output) {
      setResult(output.images);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <aside className="relative flex h-full w-full max-w-lg flex-col overflow-y-auto border-l border-border bg-card shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
          <div className="flex items-center gap-2.5">
            <ImagePlus size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Gerar imagem com IA</h2>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-[hsl(var(--surface-raised))] hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        {!hasAccess && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <Sparkles size={28} className="text-primary" />
            <p className="font-semibold text-foreground">Imagens IA são um recurso Pro</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              Assine o plano Pro+IA para gerar 50 imagens por mês.
            </p>
            <a
              href="/app/plans"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
            >
              Ver planos
            </a>
          </div>
        )}

        {hasAccess && (
          <div className="flex-1 space-y-5 p-5">
            {usageLabel && <p className="text-xs text-muted-foreground">Uso: {usageLabel}</p>}

            <div className="space-y-2">
              <Label htmlFor="ai-image-url" variant="field">
                URL da imagem do produto
              </Label>
              <input
                id="ai-image-url"
                type="url"
                placeholder="https://..."
                value={sourceUrl}
                onChange={(event) => {
                  setSourceUrl(event.target.value);
                  if (event.target.value) setSourceFile(null);
                }}
                disabled={Boolean(sourceFile)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50 disabled:opacity-50"
              />
              <p className="text-xs text-muted-foreground">ou envie um arquivo (JPG, PNG, WEBP, até 8MB)</p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => setSourceFile(event.target.files?.[0] ?? null)}
                className="w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-[hsl(var(--surface-raised))] file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label variant="field">Cenário</Label>
              <div className="grid grid-cols-2 gap-2">
                {SCENE_PRESET_OPTIONS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setPresetId(preset.id)}
                    className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                      presetId === preset.id && !customPrompt.trim()
                        ? "border-primary/60 bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {canCustomizePrompt && (
              <div className="space-y-2">
                <Label htmlFor="ai-image-custom-prompt" variant="field">
                  Ou descreva o ambiente (exclusivo Pro+IA)
                </Label>
                <textarea
                  id="ai-image-custom-prompt"
                  rows={2}
                  maxLength={300}
                  placeholder="ex: em uma academia, iluminação neon, fundo escuro"
                  value={customPrompt}
                  onChange={(event) => setCustomPrompt(event.target.value)}
                  className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label variant="field">Imagem de referência (opcional)</Label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => setReferenceFile(event.target.files?.[0] ?? null)}
                className="w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-[hsl(var(--surface-raised))] file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground"
              />
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button type="button" variant="default" className="w-full" onClick={() => void handleGenerate()} disabled={!canSubmit}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {loading ? "Gerando imagens..." : "Gerar imagens (story + feed)"}
            </Button>

            {result && (
              <div className="grid grid-cols-2 gap-3">
                {result.map((variant) => (
                  <div key={variant.variantIndex} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">
                      {variant.label === "story" ? "Story (9:16)" : "Feed (1:1)"}
                    </p>
                    {variant.signedUrl ? (
                      <a
                        href={variant.signedUrl}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="flex items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground hover:border-primary/40"
                      >
                        <Download size={13} />
                        Baixar
                      </a>
                    ) : (
                      <p className="text-xs text-destructive">Falha ao gerar este formato.</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-border pt-4">
              <AiImageGallery />
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
